/**
 * Numeric validation and formatting utilities including number parsing,
 * formatting with commas/decimals, range validation, and rounding.
 */

// Declare types

// Constants
const NUMERIC = "0123456789";
const PREFIX_SIGN = 0;
const POSTFIX_SIGN = 1;
const PAREN_SIGN = 2;

// Set on globalThis for backwards compatibility
(globalThis as Record<string, unknown>).NUMERIC = NUMERIC;
(globalThis as Record<string, unknown>).PREFIX_SIGN = PREFIX_SIGN;
(globalThis as Record<string, unknown>).POSTFIX_SIGN = POSTFIX_SIGN;
(globalThis as Record<string, unknown>).PAREN_SIGN = PAREN_SIGN;

/**
 * Extracts characters from string that match the mask
 */
function extract(str: string, mask: string): string {
  let out = "";
  for (let i = 0; i < str.length; i++) {
    if (mask.indexOf(str.charAt(i)) != -1) {
      out += str.charAt(i);
    }
  }
  return out;
}

/**
 * Extracts a clean numeric string from input, handling negative signs and parentheses
 */
function getCleanNum(strNum: string): string {
  if (extract(strNum, NUMERIC) == "") {
    return "";
  }
  const baseStr = extract(strNum, NUMERIC + '.-()');
  let cleanStr = extract(baseStr, NUMERIC + ".");

  if (cleanStr == "" || cleanStr.charAt(0) == ".") {
    cleanStr = "0" + cleanStr;
  }

  // Handle negative: prefix -, postfix -, or parentheses
  if (baseStr.charAt(0) == "-" || baseStr.charAt(baseStr.length - 1) == "-" ||
    (baseStr.charAt(0) == "(" && baseStr.charAt(baseStr.length - 1) == ")")) {
    cleanStr = "-" + cleanStr;
  }

  // Handle multiple decimal points - only keep first
  const decimal = cleanStr.indexOf(".");
  if (decimal != -1) {
    cleanStr = cleanStr.substring(0, decimal + 1) + extract(cleanStr.substring(decimal), NUMERIC);
  }

  return cleanStr;
}

/**
 * Rounds a number string to the specified precision
 */
function round(strNum: string, precision: number): number {
  const num = parseFloat(strNum);
  let multiplier = 1;
  const sign = (num < 0) ? -1.0 : 1.0;
  const p = (precision < 0 ? -precision : precision);

  for (let i = 0; i < p; i++) multiplier *= 10;

  if (precision < 0) {
    return parseInt("0" + String(num * sign / multiplier + 0.50000000000001), 10) * multiplier * sign;
  }

  return parseInt("0" + String(num * sign * multiplier + 0.50000000000001), 10) / multiplier * sign;
}

/**
 * Converts string to float, handling clean number extraction
 */
function smartParseFloat(strNum: string): number {
  return parseFloat(getCleanNum(strNum));
}

/**
 * Interface for format options
 */
interface FormatOptions {
  precision?: number;
  pad?: number;
  commas?: boolean;
  prefix?: string;
  postfix?: string;
  signFormat?: number;
}

/**
 * Formats a number string according to options
 */
function formatNum(strNum: string, obj: FormatOptions): string {
  strNum = (obj.precision != null) ? round(strNum, obj.precision).toString() : parseFloat(strNum).toString();

  const firstDigitPos = strNum.charAt(0) == "-" ? 1 : 0;
  let decimal = strNum.indexOf(".") == -1 ? strNum.length : strNum.indexOf(".");

  // Add commas
  if (obj.commas) {
    for (let i = decimal - 3; i > firstDigitPos; i -= 3) {
      strNum = strNum.substring(0, i) + "," + strNum.substring(i);
    }
  }

  // Pad decimals
  if (obj.pad != null && obj.pad > 0) {
    decimal = strNum.indexOf('.');
    if (decimal == -1) {
      decimal = strNum.length;
      strNum += ".";
    }
    const targetLen = decimal + obj.pad + 1;
    while (targetLen > strNum.length) {
      strNum += "0";
    }
  }

  strNum = strNum.substring(firstDigitPos);

  // Add prefix/postfix for positive numbers or non-paren negative
  if (firstDigitPos == 0 || obj.signFormat != PAREN_SIGN) {
    if (obj.prefix != null) strNum = obj.prefix + strNum;
    if (obj.postfix != null) strNum += obj.postfix;
  }

  // Handle negative sign formatting
  if (firstDigitPos == 1) {
    if (obj.signFormat == POSTFIX_SIGN) {
      strNum = strNum + "-";
    } else if (obj.signFormat == PAREN_SIGN) {
      strNum = "(" + strNum + ")";
      if (obj.prefix != null) strNum = obj.prefix + strNum;
      if (obj.postfix != null) strNum += obj.postfix;
    } else {
      strNum = "-" + strNum;
    }
  }

  return strNum;
}

/**
 * Interface for validation element
 */
interface NumericValidationElement {
  precision?: number;
  pad?: number;
  commas?: boolean;
  prefix?: string;
  postfix?: string;
  signFormat?: number;
  minimum?: number | string | null;
  maximum?: number | string | null;
  converter?: (val: string) => number;
  get: () => string;
  set: (val: string) => void;
  setMessage: (msg: string) => boolean;
  compareNullable: (val: number | string | null | undefined, op: string) => boolean;
}

/**
 * Gets a value for comparison, applying converter if string
 */
function getValueFor(val: number | string | null | undefined, converter: (val: string) => number): number {
  if (typeof val === "string") {
    return converter(val);
  }
  return val as number;
}

/**
 * Checks if value is within range
 */
function inRange(elementObj: NumericValidationElement): boolean {
  const overMax = elementObj.compareNullable(elementObj.maximum, ">");
  if (elementObj.compareNullable(elementObj.minimum, "<")) {
    return elementObj.setMessage("The entry cannot be less than " + formatNum(getValueFor(elementObj.minimum, smartParseFloat).toString(), elementObj) + ".");
  }
  if (overMax) {
    return elementObj.setMessage("The entry cannot be greater than " + formatNum(getValueFor(elementObj.maximum, smartParseFloat).toString(), elementObj) + ".");
  }
  return true;
}

/**
 * Main numeric validation function
 */
function doValidateBasicNumeric(obj: NumericValidationElement, getFn?: boolean): boolean | ((value: any) => number | null) {
  if (getFn) {
    obj.converter = smartParseFloat;
    // Return compareConvertedFEW function reference from errorreporter.ts
    // This gets assigned to element.compare for numeric comparisons
    return (globalThis as any).compareConvertedFEW;
  }
  const cleanStr = getCleanNum(obj.get());
  if (cleanStr == "") return obj.setMessage("This is a required field.");
  obj.set(formatNum(cleanStr, obj));
  return inRange(obj);
}

/**
 * Validates a number
 */
function validateNumber(this: NumericValidationElement, getFn?: boolean): boolean | ((value: any) => number | null) {
  return doValidateBasicNumeric(this, getFn);
}

/**
 * Validates an integer
 */
function validateInteger(this: NumericValidationElement, getFn?: boolean): boolean | ((value: any) => number | null) {
  this.precision = 0;
  this.pad = 0;
  return doValidateBasicNumeric(this, getFn);
}

/**
 * Validates money (2 decimal places)
 */
function validateMoney(this: NumericValidationElement, getFn?: boolean): boolean | ((value: any) => number | null) {
  this.precision = 2;
  this.pad = 2;
  return doValidateBasicNumeric(this, getFn);
}

/**
 * Validates whole dollars
 */
function validateWholeDollars(this: NumericValidationElement, getFn?: boolean): boolean | ((value: any) => number | null) {
  this.precision = 0;
  this.pad = 2;
  return doValidateBasicNumeric(this, getFn);
}

/**
 * Extended interface for number list validation
 */
interface NumberListValidationElement extends NumericValidationElement {
  minimum?: number | string | null;
  maximum?: number | string | null;
}

/**
 * Validates a list of numbers
 */
function validateNumberList(this: NumberListValidationElement): boolean {
  let newNums = "";
  let token: string | null;
  let cleanStr: string;
  let num: number;
  const tokenizer = new (globalThis as any).Tokenizer(this.get(), this.commas ? "; " : ",; ");

  for (token = tokenizer.nextToken(); token != null; token = tokenizer.nextToken()) {
    cleanStr = getCleanNum(token);
    if (cleanStr != "") {
      num = smartParseFloat(cleanStr);
      if (typeof this.minimum == "string") this.minimum = smartParseFloat(this.minimum);
      if (typeof this.maximum == "string") this.maximum = smartParseFloat(this.maximum);

      if (this.minimum != null && num < (this.minimum as number)) {
        return this.setMessage("The entry cannot be less than " + formatNum(smartParseFloat(this.minimum + "") + "", this) + ".");
      }
      if (this.maximum != null && num > (this.maximum as number)) {
        return this.setMessage("The entry cannot be greater than " + formatNum(smartParseFloat(this.maximum + "") + "", this) + ".");
      }

      token = formatNum(cleanStr, this);
      if (newNums.length > 0) newNums += this.commas ? "; " : ", ";
      newNums += token;
    }
  }

  this.set(newNums);
  if (newNums == "") return this.setMessage("This is a required field.");
  return true;
}

// Export for module usage
export {
  validateNumber,
  validateInteger,
  validateMoney,
  validateWholeDollars,
  validateNumberList,
  doValidateBasicNumeric,
  getCleanNum,
  round,
  smartParseFloat,
  formatNum,
  inRange,
  getValueFor,
  extract,
  NUMERIC,
  PREFIX_SIGN,
  POSTFIX_SIGN,
  PAREN_SIGN
};

// Expose on globalThis for backwards compatibility
(globalThis as Record<string, unknown>).validateNumber = validateNumber;
(globalThis as Record<string, unknown>).validateInteger = validateInteger;
(globalThis as Record<string, unknown>).validateMoney = validateMoney;
(globalThis as Record<string, unknown>).validateWholeDollars = validateWholeDollars;
(globalThis as Record<string, unknown>).validateNumberList = validateNumberList;
(globalThis as Record<string, unknown>).doValidateBasicNumeric = doValidateBasicNumeric;
(globalThis as Record<string, unknown>).getCleanNum = getCleanNum;
(globalThis as Record<string, unknown>).round = round;
(globalThis as Record<string, unknown>).smartParseFloat = smartParseFloat;
(globalThis as Record<string, unknown>).formatNum = formatNum;
(globalThis as Record<string, unknown>).inRange = inRange;

// Script resource notification
if (typeof _SR_ !== 'undefined' && _SR_ !== null) {
  _SR_.notify("validatebasicnumeric.js");
}
